import { Component, type ErrorInfo, type ReactNode } from "react";
import { reportarErroRender } from "../lib/api";
import {
  CHAVE_TRAVA,
  ehErroDeVersaoNova,
  jaRecarregouPorVersao,
  recarregarUmaVez,
} from "../lib/versaoNova";

type Props = {
  children: ReactNode;
  /** Muda de valor quando a rota muda → reseta o boundary ao navegar. */
  resetKey?: string;
};

type State = { erro: Error | null; versaoNova: boolean };

/**
 * Captura erros de RENDER (não pega erros assíncronos / de event handler —
 * esses já caem no tratamento do react-query). Sem isso, qualquer exceção
 * de render derrubava o app inteiro pra tela branca. Aqui mostramos uma
 * mensagem amigável e mantemos a sidebar/topo vivos (envolve só o <main>).
 *
 * 🔴 29/07 — ESTE É O BOUNDARY QUE PEGA A ABA VELHA, e ninguém sabia.
 * Existe um `ChunkErrorBoundary` escrito pra tratar "o deploy trocou os chunks"
 * (App.tsx:356), mas ele envolve as `Routes` POR FORA e este aqui envolve o
 * `{children}` do Layout POR DENTRO. O React entrega o erro ao boundary mais
 * PRÓXIMO — então toda página autenticada que falhava por chunk sumido caía
 * aqui, virava registro 500 em `app_errors` e mostrava "Algo deu errado nesta
 * tela". Foram 13 dos 13 "500" da semana. O reconhecimento tinha que morar
 * onde o erro cai; o `ChunkErrorBoundary` segue cobrindo as páginas públicas
 * (/p/, /medicao/, /confirmar/, /selo/), que não têm Layout, e os dois agora
 * dividem a MESMA regra (`lib/versaoNova`) — sem segunda cópia pra divergir.
 */
export class ErrorBoundary extends Component<Props, State> {
  state: State = { erro: null, versaoNova: false };

  static getDerivedStateFromError(erro: Error): State {
    return { erro, versaoNova: ehErroDeVersaoNova(erro) };
  }

  componentDidCatch(erro: Error, info: ErrorInfo) {
    if (ehErroDeVersaoNova(erro)) {
      // NÃO ENGOLE: continua registrando (foi assim que o defeito apareceu),
      // mas com classe própria — a Saúde do Sistema deixa de chamar isso de 500.
      void reportarErroRender(erro.message, "VERSAO");
      // Resolve sozinho. Devolve false quando a trava já está armada; aí o
      // render mostra a saída manual em vez de recarregar de novo (anti-laço).
      recarregarUmaVez();
      return;
    }
    // Reporta pro app_errors (feed de saúde do Dashboard) — antes o erro de
    // render morria aqui e o dono nunca ficava sabendo da tela quebrada.
    const stack = (info.componentStack ?? "").trim().split("\n")[0] ?? "";
    void reportarErroRender(`${erro.message}${stack ? ` @ ${stack.trim()}` : ""}`);
  }

  componentDidUpdate(prev: Props) {
    // Ao trocar de página, limpa o erro pra tentar renderizar a nova tela.
    if (prev.resetKey !== this.props.resetKey && this.state.erro) {
      this.setState({ erro: null, versaoNova: false });
    }
  }

  render() {
    // Aba velha ≠ tela quebrada. Nunca acusa o sistema por um deploy.
    if (this.state.erro && this.state.versaoNova) {
      // `jaRecarregouPorVersao()` já é true na SEGUNDA vez porque a marca foi
      // gravada antes do reload e sobrevive a ele (sessionStorage).
      if (!jaRecarregouPorVersao()) {
        return (
          <div className="flex flex-col items-center justify-center min-h-[60vh] px-6 text-center">
            <div className="text-4xl mb-3">🔄</div>
            <h2 className="text-lg font-semibold">Saiu uma versão nova — atualizando…</h2>
          </div>
        );
      }
      return (
        <div className="flex flex-col items-center justify-center min-h-[60vh] px-6 text-center">
          <div className="text-4xl mb-3">🔄</div>
          <h2 className="text-lg font-semibold mb-1">Saiu uma versão nova</h2>
          <p className="text-sm text-muted-foreground max-w-md mb-4">
            Não consegui atualizar sozinho. Recarregue a página para continuar.
          </p>
          <button
            onClick={() => {
              try {
                sessionStorage.removeItem(CHAVE_TRAVA);
              } catch {
                /* sem storage, o reload abaixo ainda resolve */
              }
              window.location.reload();
            }}
            className="px-4 py-2 text-sm rounded-md bg-primary text-primary-foreground hover:opacity-90"
          >
            Recarregar agora
          </button>
        </div>
      );
    }
    if (this.state.erro) {
      return (
        <div className="flex flex-col items-center justify-center min-h-[60vh] px-6 text-center">
          <div className="text-4xl mb-3">⚠️</div>
          <h2 className="text-lg font-semibold mb-1">Algo deu errado nesta tela</h2>
          <p className="text-sm text-muted-foreground max-w-md mb-4">
            O resto do painel continua funcionando. Tente recarregar a página
            ou voltar e abrir de novo. Se persistir, avise o suporte.
          </p>
          <div className="flex gap-2">
            <button
              onClick={() => this.setState({ erro: null, versaoNova: false })}
              className="px-4 py-2 text-sm rounded-md border hover:bg-muted"
            >
              Tentar de novo
            </button>
            <button
              onClick={() => window.location.reload()}
              className="px-4 py-2 text-sm rounded-md bg-primary text-primary-foreground hover:opacity-90"
            >
              Recarregar
            </button>
          </div>
          <pre className="mt-4 text-[11px] text-muted-foreground max-w-md overflow-auto">
            {this.state.erro.message}
          </pre>
        </div>
      );
    }
    return this.props.children;
  }
}
